import { Alert } from '@billda/design-system';
import { UseFormReturn } from 'react-hook-form';
import { StepNavigation } from '../../molecules/StepNavigation';
import GangMemberSelection from '../GangMemberSelection';
import {
  GangCreationFormData,
  GangParticipant,
} from '../../../types/gangCreationTypes';
export interface GangMembersStepProps {
  form: UseFormReturn<GangCreationFormData>;
  onNext: () => void;
  onBack: () => void;
  /** IDs of members that exist in the database (from initial gang data) */
  initialMemberIds?: string[];
}

export const GangMembersStep = ({
  form,
  onNext,
  onBack,
  initialMemberIds = [],
}: GangMembersStepProps) => {
  const leaderIds = form.watch('leaderIds') ?? [];
  const memberIds = form.watch('memberIds') ?? [];
  const participants = form.watch('participants') ?? {};

  // Minimum requirements: at least 2 total members (leaders + members) and at least 1 leader
  const totalMembers = leaderIds.length + memberIds.length;
  const meetsMinimumRequirements = totalMembers >= 2 && leaderIds.length >= 1;

  const canNavigate = meetsMinimumRequirements;

  const ensureParticipant = (
    userId: string,
    defaults: Pick<GangParticipant, 'fullName' | 'email' | 'canSeeSplit'>
  ) => {
    const currentParticipants = form.getValues('participants');
    const existing = currentParticipants[userId];

    const updatedParticipant: GangParticipant = {
      userId,
      fullName: defaults.fullName ?? existing?.fullName ?? '',
      email: defaults.email ?? existing?.email ?? '',
      canSeeSplit: existing?.canSeeSplit ?? defaults.canSeeSplit ?? false,
      paymentType: existing?.paymentType ?? 'percentage',
      defaultSplitPercentage: existing?.defaultSplitPercentage ?? null,
      defaultRate: existing?.defaultRate ?? null,
      defaultUnitsValue: existing?.defaultUnitsValue ?? null,
    };

    form.setValue(
      'participants',
      {
        ...currentParticipants,
        [userId]: updatedParticipant,
      },
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const handleSelectMember = (user: {
    id: string;
    fullName: string;
    email: string;
  }) => {
    ensureParticipant(user.id, {
      fullName: user.fullName,
      email: user.email,
      canSeeSplit: false,
    });

    const currentMemberIds = form.getValues('memberIds');
    if (!currentMemberIds.includes(user.id)) {
      form.setValue('memberIds', [...currentMemberIds, user.id], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    const currentLeaderIds = form.getValues('leaderIds');
    if (currentLeaderIds.includes(user.id)) {
      form.setValue(
        'leaderIds',
        currentLeaderIds.filter(id => id !== user.id),
        { shouldDirty: true, shouldValidate: true }
      );
    }
  };

  const handleRemoveMember = (userId: string) => {
    const currentMemberIds = form.getValues('memberIds');
    const nextMemberIds = currentMemberIds.filter(id => id !== userId);
    form.setValue('memberIds', nextMemberIds, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const currentLeaderIds = form.getValues('leaderIds');
    if (!currentLeaderIds.includes(userId)) {
      const nextParticipants = { ...form.getValues('participants') };
      if (nextParticipants[userId]) {
        delete nextParticipants[userId];
        form.setValue('participants', nextParticipants, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  };

  return (
    <div className="py-4">
      <p className="type-body-md mb-6 text-content-secondary">
        Add additional gang members. You can configure permissions and rates in
        the next step.
      </p>

      <GangMemberSelection
        selectedIds={memberIds}
        participants={participants}
        excludeUserIds={leaderIds}
        onSelectUser={handleSelectMember}
        onRemoveUser={handleRemoveMember}
        persistedMemberIds={initialMemberIds}
        isRemovalEnabled={true}
      />

      {memberIds.length > 0 && (
        <Alert
          severity="info"
          className="mb-4 mt-4"
          data-testid="gang-members-selected-alert"
        >
          {memberIds.length} member
          {memberIds.length === 1 ? '' : 's'} selected
        </Alert>
      )}

      <StepNavigation
        onPrevious={onBack}
        onNext={onNext}
        nextLabel="Next: Configure Members"
        disableNext={!canNavigate}
      />
    </div>
  );
};
